/**
 * Деталь заявки для менеджерского канбана (#34): GET /v1/requests/{id}
 * (бэк отдаёт { request, comments }; staff/management видит любую заявку
 * тенанта — IDOR-гейт #329 пускает персонал). Добавление комментария —
 * POST /v1/requests/{id}/comments.
 *
 * Отдельный хук (не гостевой features/requests/useRequests) ради branch-
 * isolation: канбан владеет только своими файлами.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addRequestComment, assignRequest, getAssignableStaff, getRequest } from '@hsc/api-client';
import type { AssignableStaffMember, RequestComment, RequestWithComments } from '@hsc/types';

/** GET /v1/requests/{id} — заявка + переписка для панели деталей канбана. */
export function useKanbanRequestDetail(id: string) {
  return useQuery<RequestWithComments>({
    queryKey: ['requests', 'kanban', 'detail', id],
    queryFn: () => getRequest(id),
    enabled: id !== '',
  });
}

/** POST /v1/requests/{id}/comments — добавить комментарий из панели деталей. */
export function useKanbanAddComment(id: string) {
  const qc = useQueryClient();
  return useMutation<RequestComment, Error, string>({
    mutationFn: (body) => addRequestComment(id, { body }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['requests', 'kanban', 'detail', id] });
    },
  });
}

/**
 * GET /v1/requests/{id}/assignable-staff — сотрудники, ПРАВОМОЧНЫЕ исполнять
 * именно эту заявку (§6.2 п.3): отдел категории заявки + общий пул
 * guest_services. Дропдаун исполнителя берёт список ТОЛЬКО отсюда (а не весь
 * персонал отеля). open_load — «N заявок в работе» для подписи.
 */
export function useAssignableStaff(id: string) {
  return useQuery<AssignableStaffMember[]>({
    queryKey: ['requests', 'kanban', 'assignable', id],
    queryFn: async () => (await getAssignableStaff(id)).items,
    enabled: id !== '',
    staleTime: 30_000,
  });
}

/**
 * POST /v1/requests/{id}/assign — менеджер назначает/меняет исполнителя
 * заявки (правка Артёма «менеджер должен уметь назначать/менять
 * исполнителя»). staffId — user_id сотрудника (assigned_staff_id keyed on
 * user_id, см. #274). После успеха инвалидируем доску И деталь, чтобы имя
 * исполнителя обновилось на карточке и в панели.
 */
export function useAssignRequest(id: string) {
  const qc = useQueryClient();
  return useMutation<void, Error, string>({
    mutationFn: (staffId) => assignRequest(id, staffId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['requests', 'kanban'] });
      void qc.invalidateQueries({ queryKey: ['requests', 'admin'] });
      void qc.invalidateQueries({ queryKey: ['requests', 'kanban', 'detail', id] });
    },
  });
}
